/**
 * Resolves the contact -> conversation dossier link.
 *
 * `/crm/contact/:id` (this app) and `/leads/:heyreachLeadId` (the HeyReach
 * conversation dossier) are two unlinked pages describing the same human —
 * this hook is the only bridge between them. It looks up the most recent
 * `os_conversation_threads` row for the contact (a real FK with an index,
 * see `supabase/migrations/017_conversation_threads.sql:19,82`) and falls
 * back to matching a thread's `heyreach_lead_id` against the contact's own
 * `external_contact_id` when the contact was sourced from HeyReach.
 *
 * The return shape has every field optional on purpose: a caller that gets
 * back `{}` — no thread found, or a query response shaped differently than
 * expected (e.g. an e2e fixture that ignores most filters) — degrades to
 * "no link" instead of throwing.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ContactThreadLinkInput {
  id: string;
  external_contact_id: string | null;
  external_source: string | null;
}

export interface ContactThreadLink {
  heyreachLeadId?: string;
}

interface ThreadLeadIdRow {
  heyreach_lead_id: string;
}

async function findHeyreachLeadIdByContactId(contactId: string): Promise<string | undefined> {
  const { data, error } = await supabase
    .from('os_conversation_threads')
    .select('heyreach_lead_id')
    .eq('contact_id', contactId)
    .order('last_message_at', { ascending: false })
    .limit(1);

  if (error) throw error;

  const rows = data as ThreadLeadIdRow[] | null;
  return rows?.[0]?.heyreach_lead_id;
}

async function findHeyreachLeadIdByExternalContactId(
  externalContactId: string,
): Promise<string | undefined> {
  const { data, error } = await supabase
    .from('os_conversation_threads')
    .select('heyreach_lead_id')
    .eq('heyreach_lead_id', externalContactId)
    .order('last_message_at', { ascending: false })
    .limit(1);

  if (error) throw error;

  const rows = data as ThreadLeadIdRow[] | null;
  return rows?.[0]?.heyreach_lead_id;
}

export function useContactThreadLink(
  contact: ContactThreadLinkInput | null | undefined,
): UseQueryResult<ContactThreadLink> {
  const contactId = contact?.id;
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ContactThreadLink>({
    // Tenancy sweep: identity keys the cache. `external_contact_id` is a HeyReach-global value, so
    // the lookup below can resolve across organizations in principle; RLS bounds the ROWS but does
    // not partition a browser cache across an identity switch in one session.
    queryKey: ['crm', 'contact-thread-link', userId, organizationId, contactId ?? ''],
    queryFn: async () => {
      const byContactId = await findHeyreachLeadIdByContactId(contactId!);
      if (byContactId) return { heyreachLeadId: byContactId };

      if (contact?.external_source === 'heyreach' && contact.external_contact_id) {
        const byExternalId = await findHeyreachLeadIdByExternalContactId(
          contact.external_contact_id,
        );
        if (byExternalId) return { heyreachLeadId: byExternalId };
      }

      return {};
    },
    enabled: !!contactId && userId !== null && organizationId !== null,
  });
}
